import React, { useState } from "react";
import { Combobox } from "@headlessui/react";

export interface AutocompleteItem {
  id: string;
  name: string;
  logoUrl: string;
  trailingItemElem?: React.ReactNode;
}

export default function Autocomplete({
  items,
  selectedItem,
  onSelect,
  leadingElement,
  leadingInputPaddingPx,
  trailingElement,
}: {
  items: AutocompleteItem[];
  selectedItem: AutocompleteItem | null | undefined;
  onSelect: (item: AutocompleteItem) => Promise<void>;
  leadingElement?: React.ReactNode;
  leadingInputPaddingPx?: number;
  trailingElement?: React.ReactNode;
}) {
  const [query, setQuery] = useState("");

  // limit the number of items to 10
  const filteredItems =
    query === ""
      ? []
      : items
          .filter((item) => {
            return item.name.toLowerCase().includes(query.toLowerCase());
          })
          .slice(0, 10);

  return (
    <Combobox onChange={onSelect} value={selectedItem}>
      <div>
        <div className="grid grid-cols-1 grow">
          <Combobox.Input
            autoFocus={false}
            className="col-start-1 row-start-1 w-full border-t border-r border-b rounded-r-sm !border-neutral-400 focus:!border-neutral-400 focus:!outline-none focus-visible:!outline-none focus:!shadow-none focus-visible:!shadow-none"
            style={{
              paddingLeft: leadingInputPaddingPx,
            }}
            placeholder="Start typing to search vendors by name"
            displayValue={(item: AutocompleteItem) => (item ? item.name : "")}
            onChange={(event) => setQuery(event.target.value)}
          />
          {/* pointer-events-none is necessary so clicks pass through to the input */}
          <div className="col-start-1 row-start-1 pointer-events-none">{leadingElement}</div>
          <div className="col-start-1 row-start-1 justify-self-end">{trailingElement}</div>
        </div>
      </div>

      <Combobox.Options
        className="absolute w-[650px] max-h-[264px] scroll-py-3 overflow-y-auto p-3 bg-white rounded-md"
        style={{
          scrollbarWidth: "none",
          boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.25)",
        }}
      >
        {filteredItems.map((item) => (
          <Combobox.Option key={item.id} value={item} as={"div"}>
            {({ active }) => (
              <div
                className={`flex items-center gap-2 mb-1 px-4 py-5 border border-solid ${
                  active ? "border-neutral-900" : "border-neutral-200"
                } rounded`}
              >
                <img src={item.logoUrl} alt={item.name} className="h-6 w-6" />
                <div className={`${active ? "font-medium" : ""}`}>{item.name}</div>
                {item.trailingItemElem && <div className="ml-[12px]">{item.trailingItemElem}</div>}
              </div>
            )}
          </Combobox.Option>
        ))}
      </Combobox.Options>
    </Combobox>
  );
}
